import React from "react";
import zoomSdk from "@zoom/appssdk";

import Box from "@amzn/meridian/esm/components/box";
import Column from "@amzn/meridian/esm/components/column";
import Row from "@amzn/meridian/esm/components/row";
import Text from "@amzn/meridian/esm/components/text";
import Icon from "@amzn/meridian/esm/components/icon";
import Link from "@amzn/meridian/esm/components/link";
import Theme from "@amzn/meridian/esm/components/theme";

import alertFilledTokens from "@amzn/meridian-tokens/base/icon/alert-knockout";
import errorKnockout from "@amzn/meridian-tokens/base/icon/error-knockout";
import lineIcon from "../../../images/line-summary-sections.svg";
import LogManager from "../../utils/LogManager";

/**
 * SummaryBodyInDetailPage Component
 *
 * Renders the detailed body content of a meeting summary in the summary detail page.
 * This component encapsulates all conditional rendering logic based on `summary.summaryType`,
 * such as displaying warnings or error messages, or the full summary content including:
 * - Narrative summary
 * - Decisions (as bullet points)
 * - Next Steps (as bullet points)
 * - A footer note about AI-generated content
 *
 * Special Cases Handled:
 * - "privileged_and_confidential": Summary for host-only
 * - "server_error": Display server issue notice
 * - "short_meeting": Show warning for meetings too short to summarize
 * - "not_enough_audio": Show warning about missing conversation audio
 * - Meeting topics not approved: Link to internal documentation
 *
 * Props:
 * - `summary` (object): Required. Meeting summary metadata, narrative, decisions, and next steps.
 * - isTopicNotApproved (boolean): Indicates if the meeting topic is not approved
 */
const SummaryBodyInDetailPage = ({ summary, isTopicNotApproved }) => {
  const SummaryMessage = ({ icon, color, children }) => (
    <Row width="100%" widths={["16px", "8px", "282px"]} wrap="none" alignmentHorizontal="start" spacing="none" alignmentVertical="top">
      <Icon tokens={icon} color={color} />
      <Row width="8px" />
      <Box width="282px" height="64px">
        <Text type="b100" fontFamily="amazonEmber">
          {children}
        </Text>
      </Box>
    </Row>
  );

  const showOrganizerOnly = summary.summaryType === "privileged_and_confidential" && summary.role !== "host";
  const isServerError = summary.summaryType === "server_error";
  const isTooShort = summary.summaryType === "short_meeting";
  const isNotEnoughAudio = summary.summaryType === "not_enough_transcript";

  const handleLearnMoreClick = async () => {
    try {
      await zoomSdk.openUrl({ url: "https://w.amazon.com/bin/view/UnifiedCommunications/MeetingSummaries/AiMS" });
    } catch (error) {
      LogManager.error("Failed to open URL in Zoom:", error);
    }
  };

  if (showOrganizerOnly) {
    return (
      <SummaryMessage icon={alertFilledTokens} color="warning">
        Contact meeting organizer if you want to see the meeting summary.
      </SummaryMessage>
    );
  }

  if (isServerError) {
    return (
      <SummaryMessage icon={errorKnockout} color="error">
        There is no meeting summary because there were issues with the server.
      </SummaryMessage>
    );
  }

  if (isTopicNotApproved) {
    return (
      <Column spacing="none" width="306px">
        <SummaryMessage icon={errorKnockout} color="error">
          There is no meeting summary because the meeting topics are not approved for Amazon Meetings Summary.
        </SummaryMessage>
        <Link onClick={handleLearnMoreClick} type="secondary">
          <Theme tokens={{ textColorPrimary: "#006D5B" }}>
            <Text color="primary" type="b200">
              <u>Learn more</u>
            </Text>
          </Theme>
        </Link>
      </Column>
    );
  }

  if (isTooShort) {
    return (
      <SummaryMessage icon={errorKnockout} color="error">
        There is no meeting summary because the summarization was too short.
        <br />A meeting summarization needs to take at least 2 minutes to create a summary.
      </SummaryMessage>
    );
  }

  if (isNotEnoughAudio) {
    return (
      <SummaryMessage icon={errorKnockout} color="error">
        There is no meeting summary because we didn’t hear enough conversation.
        <br />
        This may be due to microphone issues.
      </SummaryMessage>
    );
  }

  return (
    <>
      {summary.summaryType === "privileged_and_confidential" && summary.role === "host" && (
        <SummaryMessage icon={alertFilledTokens} color="warning">
          [Privileged and Confidential]
          <br />
          You are the only one who receives this meeting summary. Seek legal approval before sharing.
        </SummaryMessage>
      )}

      {/* narrative Summary */}
      <Box width="306px">
        <Text type="b100" color="secondary" fontFamily="amazonEmber">
          {summary.narrativeSummary}
        </Text>
      </Box>

      <Column height="24px" />
      <Column spacing="none" spacingInset="none">
        {/* Decisions Box*/}
        <Box width="326px">
          <Column spacing="none" spacingInset="none">
            {/*Decisions title*/}
            <Row spacing="none" spacingInset="none">
              <Row width="8px">
                <img src={lineIcon} alt="lineIcon" style={{ display: "block", margin: "auto", maxWidth: "100%", height: "auto" }} />
              </Row>
              <Text type="h50" fontFamily="amazonEmber">
                Decisions
              </Text>
            </Row>

            <Column height="16px" />
            {/*Decisions bullet points */}
            <Box width="306px" spacing="none" spacingInset="none">
              <ul style={{ paddingLeft: "20px", margin: 0 }}>
                {summary.decisions &&
                  summary.decisions.map((decision, index) => (
                    <li key={index}>
                      <Text type="b100" fontFamily="amazonEmber">
                        {decision}
                      </Text>
                    </li>
                  ))}
              </ul>
            </Box>

            <Column height="24px" />

            {/* Next Steps title */}
            <Row spacing="none" spacingInset="none">
              <Row width="8px">
                <img src={lineIcon} alt="lineIcon" style={{ display: "block", margin: "auto", maxWidth: "100%", height: "auto" }} />
              </Row>
              <Text type="h50" fontFamily="amazonEmber">
                Next Steps
              </Text>
            </Row>

            {/* Next Steps bullet points */}
            <Column height="16px" />

            <Box width="306px" spacing="none" spacingInset="none">
              <ul style={{ paddingLeft: "20px", margin: 0 }}>
                {summary.nextSteps &&
                  summary.nextSteps.map((decision, index) => (
                    <li key={index}>
                      <Text type="b100" fontFamily="amazonEmber">
                        {decision}
                      </Text>
                    </li>
                  ))}
              </ul>
            </Box>
          </Column>
        </Box>
      </Column>

      <Column height="25px" />

      {/* AIMS Footer */}
      <Column width="306px" spacing="none" spacingInset="none">
        <Theme tokens={{ textColorPrimary: "#575F67" }}>
          <Text fontFamily="amazonEmber" type="b100" color="primary">
            <b>Amazon Meetings Summary</b> is powered by Amazon AI. Always check for accuracy.
          </Text>
        </Theme>
      </Column>
    </>
  );
};

export default SummaryBodyInDetailPage;
